import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import type { EntitySummary } from "../../shared/api";
import { EntityCard } from "../components/EntityCard";
import { Spinner, Empty, ErrorState } from "../components/States";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { useRouteData, directoryKey, categoryMetaKey, PAGE_SIZE } from "../lib/routeData";
import { origin } from "../lib/origin";
import type { EntityType } from "../../shared/dimensions";
import { MAX_COMPARE, MIN_COMPARE } from "../../shared/compare";

// Used for /tokens and /companies (type fixed) and /category/:slug (type undefined —
// the category join already scopes results to its own type).
export function Directory({ type }: { type?: EntityType }) {
  const params = useParams();
  const category = params.slug;
  const [sp, setSp] = useSearchParams();
  const sort = sp.get("sort") ?? "rating";
  const pageParam = parseInt(sp.get("page") ?? "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 1 ? pageParam : 1;

  const [cats, setCats] = useState<{ slug: string; name: string }[]>([]);
  const [promoted, setPromoted] = useState<{ items: EntitySummary[]; disclosure: string } | null>(null);
  const [catName, setCatName] = useState<string | null>(null);
  const [catDesc, setCatDesc] = useState<string | null>(null);

  // Seeded by the server so the intro is in the HTML a crawler reads, not
  // painted in afterwards.
  const { data: catMeta } = useRouteData<{ name: string; description: string | null }>(
    categoryMetaKey(category ?? ""),
    () => api.category(category!),
    { enabled: !!category },
  );

  const { data, error, loading } = useRouteData<{ items: EntitySummary[]; total: number }>(
    directoryKey(type, category, sort, page),
    () =>
      api.directory({
        type,
        category,
        sort,
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }),
  );
  const items = data?.items ?? null;
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Real links, not buttons. Everything past the first 24 listings had nothing
  // pointing at it — no crawler could reach it and no reader could either.
  const hrefFor = (n: number) => {
    const next = new URLSearchParams(sp);
    if (n > 1) next.set("page", String(n));
    else next.delete("page");
    const qs = next.toString();
    return `${category ? `/category/${category}` : type === "token" ? "/tokens" : "/companies"}${qs ? `?${qs}` : ""}`;
  };

  const heading = catName ?? (type === "token" ? "Tokens & coins" : type === "company" ? "Companies & projects" : "Browse");

  const intro =
    catDesc ??
    (category
      ? null
      : type === "token"
        ? "Tokens and coins, each with the contract address a reader can check on a block explorer, an automated security scan, and reviews split into those backed by proof of holding and those that are not."
        : type === "company"
          ? "Exchanges, wallets, custodians and protocols. Reviews here can carry proof that the reviewer held an account or signed from a wallet, and every listing shows how many of its reviews clear that bar."
          : null);

  useMeta(
    category
      ? metaFor({
          route: "category",
          base: origin(),
          slug: category,
          name: heading,
          items: items ?? [],
          page,
          pageCount,
        })
      : type
        ? metaFor({ route: "directory", base: origin(), type, total, page, pageCount })
        : null,
  );

  useEffect(() => {
    if (type) api.categories(type).then((r) => setCats(r.items)).catch(() => {});
  }, [type]);

  useEffect(() => {
    api
      .featured({ type, category })
      .then(setPromoted)
      .catch(() => setPromoted(null));
  }, [type, category]);

  useEffect(() => {
    if (!category) {
      setCatName(null);
      setCatDesc(null);
      return;
    }
    if (catMeta) {
      setCatName(catMeta.name);
      setCatDesc(catMeta.description);
    }
  }, [category, catMeta]);

  return (
    <div>
      <h1>{heading}</h1>
      {intro && <p className="directory-intro">{intro}</p>}
      <div className="toolbar">
        {!category && type && (
          <div className="pill-group">
            {cats.slice(0, 6).map((c) => (
              <a key={c.slug} href={`/category/${c.slug}`}>{c.name}</a>
            ))}
          </div>
        )}
        <div className="header-spacer" />
        <label className="muted u-1e8f47b">Sort</label>
        <select
          className="input"
          value={sort}
          onChange={(e) =>
            setSp((p) => {
              p.set("sort", e.target.value);
              p.delete("page");
              return p;
            })
          }
        >
          <option value="rating">Highest rated</option>
          <option value="trust">Trust score</option>
          <option value="reviews">Most reviewed</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      {promoted && promoted.items.length > 0 && (
        <section className="featured-block">
          {/* The disclosure comes from the server verbatim: a client-side
              paraphrase could drift from what the label is supposed to say. */}
          <p className="disclosure">{promoted.disclosure}</p>
          <div className="grid card-grid">
            {promoted.items.map((e) => <EntityCard key={e.id} e={e} />)}
          </div>
        </section>
      )}

      {items && items.length >= MIN_COMPARE ? (
        <p className="muted u-348d852">
          <Link to={`/compare?ids=${items.slice(0, MAX_COMPARE).map((e) => e.id).join(",")}`}>
            Compare the top {Math.min(MAX_COMPARE, items.length)} side by side →
          </Link>
        </p>
      ) : null}

      {error ? (
        <ErrorState message={error} />
      ) : loading || !items ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty title="Nothing here yet" hint="Be the first to review something in this space." />
      ) : (
        <>
          <div className="grid card-grid">
            {items.map((e) => <EntityCard key={e.id} e={e} />)}
          </div>

          {pageCount > 1 && (
            <nav className="pager" aria-label="Pagination">
              {page > 1 ? (
                <Link className="btn btn-ghost btn-sm" rel="prev" to={hrefFor(page - 1)}>
                  ← Previous
                </Link>
              ) : (
                <span />
              )}

              <span className="muted pager-count">
                Page {page} of {pageCount} · {total} listings
              </span>

              {page < pageCount ? (
                <Link className="btn btn-ghost btn-sm" rel="next" to={hrefFor(page + 1)}>
                  Next →
                </Link>
              ) : (
                <span />
              )}
            </nav>
          )}
        </>
      )}
    </div>
  );
}
